import { clsx } from 'clsx';
import styles from './EviMapDot.module.css';

/**
 * EVI glowing map marker — grounded in the production
 * evinitiative.com CSS system.
 *
 * Three layered shadows give it the breathing-halo feel the
 * marketing site uses for its hero accents:
 *   - 2px inner rim
 *   - short 12px brand-green glow
 *   - long 22px falloff
 * At 50% the scale bumps to 1.45× and the halos double + extend,
 * producing the "phase-dot-breath" pulse.
 *
 * Ambient animation runs at 2.4s (per Warren: dense views with many
 * dots would feel busy at the 1.6s hero cadence). Hover / selected
 * states drop to 1.6s and layer the "phase-ring-ping" expanding
 * ring on top — a slower-dot baseline with a deliberate pop when
 * the driver is picking one.
 *
 * `count` (optional): when >1, renders the number centered in the
 * dot and bumps the visual size slightly. Used by the admin
 * dashboard's adaptive cluster rendering (single dot for 1 charger
 * at a location, counted dot for N chargers at a location).
 *
 * `tone` (optional): brand-green is the default. `muted` drops the
 * glow and dims the core for offline chargers; `warn` shifts to the
 * warning token for faulted-but-online cases; `guest` paints the
 * amber token for cross-tenant guest-eligible chargers (third-party
 * access ticket — visual signal that the charger is reachable but
 * carries guest pricing and access constraints); `occupied` paints
 * blue to signal a location whose stalls are all in active use,
 * paired with the matching "Charging" StatusPill blue on the
 * station-detail screen so drivers learn one color = active session.
 *
 * `partialActive` (optional): layers a blue expanding ring on top
 * of whatever tone is set. Used to signal "some stalls free, some
 * in use" — the dot color tells the driver the access story
 * (brand-green for tenant, guest-amber for cross-tenant), the
 * always-on blue ring overlay communicates the occupancy story.
 * Independent of the hover/selected `.ring` (which only renders
 * when `active` is true); the partial ring is always on while
 * partialActive is true, so the two never animate the same
 * geometry simultaneously.
 *
 * Used in both apps/admin (dashboard destination map) and apps/web
 * (driver PWA map) so the two surfaces read as one product.
 *
 * Honours `prefers-reduced-motion` — animation collapses to a
 * static halo so motion-sensitive users still see the brand glow.
 */
/** Five-bucket connector-status counts for the opt-in segmented ring. Same
 *  buckets + canonical colors as the station-detail status grid. Passed only
 *  by the driver-map cluster path; when present, EviMapDot draws a segmented
 *  ring around the core sized by these proportions. */
export interface EviMapDotStatusCounts {
  available: number;
  charging: number;
  preparing: number;
  pluggedIn: number;
  unavailable: number;
}

export interface EviMapDotProps {
  /** Cluster count. When > 1, renders the numeral and bumps size. */
  count?: number;
  /** Hover or selection state — drops animation to 1.6s + adds ring-ping. */
  active?: boolean;
  /** Tone variant. Default brand-green; `muted` for offline; `warn`
   *  for faults; `guest` for cross-tenant guest-eligible (amber);
   *  `occupied` for locations whose stalls are all actively in use
   *  (blue, matches the "Charging" StatusPill on station detail). */
  tone?: 'brand' | 'muted' | 'warn' | 'guest' | 'occupied';
  /** When true, layers a blue expanding ring overlay on top of the
   *  dot. Used by the map to signal "partially active" locations
   *  (some stalls free, some in use). Independent of `active`
   *  (hover/selected). */
  partialActive?: boolean;
  /** Accessibility label. Falls back to "Charger location" when omitted. */
  ariaLabel?: string;
  /** Opt-in status-segmented ring (driver-map clusters only). When set, a
   *  conic-gradient ring is drawn around the core, divided by these five-
   *  bucket proportions in the canonical status colors. The single dot + the
   *  admin map never pass it, so the ring never renders there. The core's
   *  glow + breathing pulse are unchanged; the ring is purely additive. */
  statusCounts?: EviMapDotStatusCounts;
}

// Canonical status colors + a fixed order for the segmented ring — the exact
// hex from the station-detail status grid (StationDetailPage.module.css), so a
// cluster's segments read as the same status code drivers see on detail.
const SEGMENT_ORDER: ReadonlyArray<readonly [keyof EviMapDotStatusCounts, string]> = [
  ['available', '#16A34A'],
  ['charging', '#F59E0B'],
  ['preparing', '#3498DB'],
  ['pluggedIn', '#64748B'],
  ['unavailable', '#DC2626'],
];

/** Build the status-ring conic-gradient from the five-bucket counts. Hard
 *  stops (no blending) so each status is a clean wedge; zero-count buckets are
 *  skipped. Returns null on an empty tally (no ring rather than an empty
 *  gradient). */
function buildStatusRingGradient(counts: EviMapDotStatusCounts): string | null {
  const total = counts.available + counts.charging + counts.preparing
    + counts.pluggedIn + counts.unavailable;
  if (total <= 0) return null;
  const stops: string[] = [];
  let acc = 0;
  for (const [key, color] of SEGMENT_ORDER) {
    const n = counts[key];
    if (n <= 0) continue;
    const start = (acc / total) * 360;
    acc += n;
    const end = (acc / total) * 360;
    stops.push(`${color} ${start}deg ${end}deg`);
  }
  return `conic-gradient(${stops.join(', ')})`;
}

export function EviMapDot({
  count,
  active = false,
  tone = 'brand',
  partialActive = false,
  ariaLabel,
  statusCounts,
}: EviMapDotProps) {
  const isCluster = typeof count === 'number' && count > 1;
  const label = ariaLabel
    ?? (isCluster ? `${count} chargers at this location` : 'Charger location');
  // Opt-in segmented ring: only when the caller passes a tally (driver
  // clusters). Null tally / empty total -> no ring; the dot is unchanged.
  const ringGradient = statusCounts ? buildStatusRingGradient(statusCounts) : null;

  return (
    <div
      className={clsx(
        styles.root,
        active && styles.active,
        isCluster && styles.cluster,
        tone === 'muted' && styles.toneMuted,
        tone === 'warn' && styles.toneWarn,
        tone === 'guest' && styles.toneGuest,
        tone === 'occupied' && styles.toneOccupied,
        partialActive && styles.partial,
      )}
      role="img"
      aria-label={label}
    >
      {/* Partial-active overlay — always on while partialActive is
          true. Layered below the hover/selected ring so the two
          rings stack cleanly when both fire (e.g., partial location
          that the driver is currently picking). */}
      {partialActive && <span className={styles.partialRing} aria-hidden="true" />}
      {active && <span className={styles.ring} aria-hidden="true" />}
      {/* Status-segmented ring — additive, sits above the glow with a donut
          hole so the solid core + count show through. Glow + core pulse
          unchanged; the ring breathes in sync via its own scale keyframe. */}
      {ringGradient && (
        <span className={styles.statusRing} style={{ background: ringGradient }} aria-hidden="true" />
      )}
      <span className={styles.core}>
        {isCluster && <span className={styles.count}>{count}</span>}
      </span>
    </div>
  );
}
